import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import Header from '../common/Header'
import SideMenu from '../common/SideMenu'
import { useTranslation } from 'react-i18next'
import './WmsSettings.css'

const WmsSettings = () => {
  const { t } = useTranslation()
  const { shop } = useParams()

  const [settings, setSettings] = useState({
    enabled: false,
    base_url: '',
    seller_account: '',
    sid: '',
    app_key: '',
    app_secret: '',
    warehouse_code: '',
    logistics_code: '',
    logistics_code_insured: '',
    insurance_threshold_points: 0,
    foreign_trade_flag: '',
    express_pdf: '',
    source_platform_code: 'ORIPAL',
    use_shop_as_sender: true,
    auto_delivery_on_entry_confirm: true,
    receiver: {
      name: '', phone: '', province: '', city: '',
      area: '', address: '', country_code: 'CN', zip: '',
    },
    sender: {
      name: '', phone: '', province: '', city: '',
      area: '', address: '', country_code: 'JP', zip: '',
    },
  })

  const [shopSender, setShopSender] = useState({})
  const [confirm, setConfirm] = useState(false)
  const [loading, setLoading] = useState(true)

  const getTokenHeaders = () => {
    let xsrfToken = document.cookie.split('; ').find(row => row.startsWith(process.env.REACT_APP_XSRF_TOKEN))
    if (xsrfToken) xsrfToken = xsrfToken.split('=')[1]
    let token = document.cookie.split('; ').find(row => row.startsWith('token'))
    if (token) token = token.split('=')[1]
    return {
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': decodeURIComponent(xsrfToken),
      'Bearer': decodeURIComponent(token),
      'Accept': 'application/json',
    }
  }

  const fetchSettings = async () => {
    try {
      const res = await fetch(process.env.REACT_APP_API_URL + '/api/wms-settings', {
        method: 'GET',
        headers: getTokenHeaders(),
        credentials: 'include',
      })
      if (res.ok) {
        const data = await res.json()
        setSettings(data.settings)
        setShopSender(data.shop_sender || {})
      }
    } catch (err) {
      console.error('Failed to fetch WMS settings', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  const handleConfirm = () => {
    const el = document.querySelector('.wms-settings')
    if (el) el.scrollTo(0, 0)
    setConfirm(true)
  }

  const handleBack = () => {
    if (confirm) {
      const el = document.querySelector('.wms-settings')
      if (el) el.scrollTo(0, 0)
      setConfirm(false)
    } else {
      window.location.href = '/' + shop + '/shop'
    }
  }

  const handleSave = async () => {
    try {
      const res = await fetch(process.env.REACT_APP_API_URL + '/api/wms-settings', {
        method: 'POST',
        headers: getTokenHeaders(),
        credentials: 'include',
        body: JSON.stringify(settings),
      })
      if (res.ok) {
        alert(t('wms_settings.saved'))
        setConfirm(false)
      } else {
        alert(t('wms_settings.save_error'))
      }
    } catch (err) {
      alert(t('wms_settings.save_error'))
    }
  }

  const updateField = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  const updateReceiver = (key, value) => {
    setSettings(prev => ({
      ...prev,
      receiver: { ...prev.receiver, [key]: value },
    }))
  }

  const updateSender = (key, value) => {
    setSettings(prev => ({
      ...prev,
      sender: { ...prev.sender, [key]: value },
    }))
  }

  const maskSecret = (value) => {
    if (!value) return ''
    if (value.length <= 6) return '******'
    return value.substring(0, 3) + '***' + value.substring(value.length - 3)
  }

  const renderField = (label, value, onChange, type = 'text') => (
    <>
      <dt>{label}</dt>
      <dd>
        {confirm
          ? (type === 'password' ? <span className="wms-settings-mask">{maskSecret(value)}</span> : <>{value}</>)
          : <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} />
        }
      </dd>
    </>
  )

  const renderContactFields = (prefix, contact, updater) => {
    const fields = [
      ['name', t('wms_settings.contact_name')],
      ['phone', t('wms_settings.contact_phone')],
      ['province', t('wms_settings.contact_province')],
      ['city', t('wms_settings.contact_city')],
      ['area', t('wms_settings.contact_area')],
      ['address', t('wms_settings.contact_address')],
      ['country_code', t('wms_settings.contact_country_code')],
      ['zip', t('wms_settings.contact_zip')],
    ]

    return fields.map(([key, label]) => (
      <React.Fragment key={`${prefix}-${key}`}>
        <dt>{label}</dt>
        <dd>
          {confirm
            ? <>{contact[key] || ''}</>
            : <input type="text" value={contact[key] || ''} onChange={e => updater(key, e.target.value)} />
          }
        </dd>
      </React.Fragment>
    ))
  }

  if (loading) return (
    <>
      <Header />
      <SideMenu />
      <div className="wms-settings">
        <div className="wms-settings-content">{t('common.loading')}</div>
      </div>
    </>
  )

  return (
    <>
      <Header />
      <SideMenu />
      <div className="wms-settings">
        <div className="wms-settings-content">
          <div className="wms-settings-title">{t('wms_settings.title')}</div>
          <div className="clearfix"></div>

          {/* 接続設定 */}
          <div className="wms-settings-sub-title">{t('wms_settings.section_connection')}</div>
          <dl className="wms-settings-info">
            <dt>{t('wms_settings.enabled')}</dt>
            <dd>
              {confirm
                ? <>{settings.enabled ? t('wms_settings.on') : t('wms_settings.off')}</>
                : <div className="wms-settings-toggle">
                    <input
                      type="checkbox"
                      checked={settings.enabled}
                      onChange={e => updateField('enabled', e.target.checked)}
                    />
                    <span>{settings.enabled ? t('wms_settings.on') : t('wms_settings.off')}</span>
                  </div>
              }
            </dd>
            {renderField(t('wms_settings.base_url'), settings.base_url, v => updateField('base_url', v))}
            {renderField(t('wms_settings.seller_account'), settings.seller_account, v => updateField('seller_account', v))}
            {renderField('SID', settings.sid, v => updateField('sid', v))}
            {renderField('App Key', settings.app_key, v => updateField('app_key', v))}
            {renderField('App Secret', settings.app_secret, v => updateField('app_secret', v), 'password')}
            <dt>{t('wms_settings.callback_url')}</dt>
            <dd>
              <input
                type="text"
                value={`${process.env.REACT_APP_API_URL}/api/wms/callback`}
                readOnly
                style={{backgroundColor: '#f3f4f6', cursor: 'default'}}
              />
            </dd>
          </dl>

          <div className="wms-settings-line"></div>

          {/* 倉庫設定 */}
          <div className="wms-settings-sub-title">{t('wms_settings.section_warehouse')}</div>
          <dl className="wms-settings-info">
            {renderField(t('wms_settings.warehouse_code'), settings.warehouse_code, v => updateField('warehouse_code', v))}
            {renderField(t('wms_settings.logistics_code'), settings.logistics_code, v => updateField('logistics_code', v))}
            {renderField(t('wms_settings.logistics_code_insured'), settings.logistics_code_insured, v => updateField('logistics_code_insured', v))}
            <dt>{t('wms_settings.insurance_threshold_points')}</dt>
            <dd>
              {confirm
                ? <>{settings.insurance_threshold_points || 0}</>
                : <input
                    type="number"
                    min="0"
                    value={settings.insurance_threshold_points || 0}
                    onChange={e => updateField('insurance_threshold_points', parseInt(e.target.value) || 0)}
                  />
              }
            </dd>
            {renderField(t('wms_settings.foreign_trade_flag'), settings.foreign_trade_flag, v => updateField('foreign_trade_flag', v))}
            {renderField(t('wms_settings.express_pdf'), settings.express_pdf, v => updateField('express_pdf', v))}
            {renderField(t('wms_settings.source_platform_code'), settings.source_platform_code, v => updateField('source_platform_code', v))}
          </dl>

          <div className="wms-settings-line"></div>

          {/* 自動出庫設定 */}
          <div className="wms-settings-sub-title">{t('wms_settings.section_auto_delivery')}</div>
          <dl className="wms-settings-info">
            <dt>{t('wms_settings.auto_delivery_on_entry_confirm')}</dt>
            <dd>
              {confirm
                ? <>{settings.auto_delivery_on_entry_confirm ? t('wms_settings.on') : t('wms_settings.off')}</>
                : <div className="wms-settings-toggle">
                    <input
                      type="checkbox"
                      checked={settings.auto_delivery_on_entry_confirm}
                      onChange={e => updateField('auto_delivery_on_entry_confirm', e.target.checked)}
                    />
                    <span>{settings.auto_delivery_on_entry_confirm ? t('wms_settings.on') : t('wms_settings.off')}</span>
                  </div>
              }
            </dd>
          </dl>

          <div className="wms-settings-line"></div>

          {/* 受取人(CN倉庫) */}
          <div className="wms-settings-sub-title">{t('wms_settings.section_receiver')}</div>
          <dl className="wms-settings-info">
            {renderContactFields('receiver', settings.receiver, updateReceiver)}
          </dl>

          <div className="wms-settings-line"></div>

          {/* 発送元(JP) */}
          <div className="wms-settings-sub-title">{t('wms_settings.section_sender')}</div>
          <dl className="wms-settings-info">
            <dt>{t('wms_settings.use_shop_as_sender')}</dt>
            <dd>
              {confirm
                ? <>{settings.use_shop_as_sender ? t('wms_settings.on') : t('wms_settings.off')}</>
                : <div className="wms-settings-toggle">
                    <input
                      type="checkbox"
                      checked={settings.use_shop_as_sender}
                      onChange={e => updateField('use_shop_as_sender', e.target.checked)}
                    />
                    <span>{settings.use_shop_as_sender ? t('wms_settings.on') : t('wms_settings.off')}</span>
                  </div>
              }
            </dd>
            {settings.use_shop_as_sender ? (
              <>
                <dt>{t('wms_settings.shop_sender_preview')}</dt>
                <dd>
                  <div className="wms-settings-shop-sender-note">{t('wms_settings.shop_sender_note')}</div>
                  <div>{t('wms_settings.contact_name')}: {shopSender.name || '-'}</div>
                  <div>{t('wms_settings.contact_province')}: {shopSender.province || '-'}</div>
                  <div>{t('wms_settings.contact_address')}: {shopSender.address || '-'}</div>
                  <div>{t('wms_settings.contact_zip')}: {shopSender.zip || '-'}</div>
                </dd>
              </>
            ) : (
              renderContactFields('sender', settings.sender, updateSender)
            )}
          </dl>
        </div>

        <div className="clearfix"></div>
        <div className="wms-settings-button">
          {confirm ? (
            <>
              <button className="wms-settings-button-back" onClick={handleBack}>{t('wms_settings.back')}</button>
              <button className="wms-settings-button-save" onClick={handleSave}>{t('wms_settings.save')}</button>
            </>
          ) : (
            <>
              <button className="wms-settings-button-back" onClick={handleBack}>{t('wms_settings.back')}</button>
              <button className="wms-settings-button-save" onClick={handleConfirm}>{t('wms_settings.confirm')}</button>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default WmsSettings
